import styled from '@emotion/styled';
import { CheckOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';

const Overlay = styled.div`
  min-width: 180px;
  padding: 10px;
  color: var(--fett-text-secondary);
  background: var(--fett-bg-panel);
  box-shadow: 0px 2px 12px 0px rgba(167, 178, 204, 0.1);
`;

const Option = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  height: 28px;
  padding: 0 8px;
  border-radius: 2px;
  cursor: pointer;
  &:hover {
    background: var(--fett-bg-hover);
  }
  > div {
    flex: 1 0 0;
    padding-right: 20px;
    white-space: nowrap;
  }
  > span {
    position: absolute;
    top: 4px;
    right: 4px;
  }
`;

const SortIcon = styled(SwapOutlined)`
  transform: rotateZ(90deg);
`;

export interface SorterProps {
  platform: Review.Platform;
  cursor: Review.Cursor[];
  onCursorChange: (cursor: Review.Cursor[]) => void;
}

function Sorter(props: SorterProps) {
  const { platform, cursor, onCursorChange } = props;

  const { t } = useTranslate();

  const options: {
    fieldName: Review.Cursor['fieldName'];
    direction: Review.Cursor['direction'];
    label: string;
  }[] = [
    {
      fieldName: 'review_rating',
      direction: 'ASC',
      label:
        platform === 'LAZADA'
          ? t('scene.reviewWorkspace.list.sort.overallRatingLowest')
          : t('scene.reviewWorkspace.list.sort.reviewRatingLowest'),
    },
    {
      fieldName: 'review_rating',
      direction: 'DESC',
      label:
        platform === 'LAZADA'
          ? t('scene.reviewWorkspace.list.sort.overallRatingHighest')
          : t('scene.reviewWorkspace.list.sort.reviewRatingHighest'),
    },
    {
      fieldName: 'spu_name',
      direction: 'ASC',
      label: t('scene.reviewWorkspace.list.sort.productRatingLowest'),
    },
    {
      fieldName: 'has_content',
      direction: 'DESC',
      label: t('scene.reviewWorkspace.list.sort.withContentFirst'),
    },
    {
      fieldName: 'has_content',
      direction: 'ASC',
      label: t('scene.reviewWorkspace.list.sort.withContentLast'),
    },
    {
      fieldName: 'comment_time',
      direction: 'ASC',
      label: t('scene.reviewWorkspace.list.sort.reviewTimeEarliest'),
    },
    {
      fieldName: 'comment_time',
      direction: 'DESC',
      label: t('scene.reviewWorkspace.list.sort.reviewTimeLatest'),
    },
  ];

  const activeOption = options.find((option) => {
    return option.fieldName === cursor[0].fieldName && option.direction === cursor[0].direction;
  });

  const optionList = options.map((option) => {
    const { fieldName, direction, label } = option;

    const active = fieldName === cursor[0].fieldName && direction === cursor[0].direction;

    const onSelect = () => {
      if (active) {
        return;
      }

      const nextCursor = cursor.reduce<Review.Cursor[]>(
        (res, cur) => {
          if (cur.fieldName !== fieldName) {
            res.push(cur);
          }

          return res;
        },
        [
          {
            fieldName,
            direction,
          },
        ],
      );

      onCursorChange(nextCursor);
    };

    return (
      <Option key={label} onClick={onSelect}>
        <div>{label}</div>
        {active && <CheckOutlined />}
      </Option>
    );
  });

  return (
    <Dropdown trigger={['click']} placement="bottomRight" overlay={<Overlay>{optionList}</Overlay>}>
      <Tooltip
        title={activeOption ? activeOption.label : t('scene.reviewWorkspace.list.action.sort')}
      >
        <Button type="text">
          <div className="!text-[var(--fett-text-secondary)] inline-flex items-center gap-0.5">
            <SortIcon />
            <span className="inline-block max-w-44 overflow-hidden text-ellipsis whitespace-nowrap">
              {activeOption ? activeOption.label : null}
            </span>
          </div>
        </Button>
      </Tooltip>
    </Dropdown>
  );
}

export default Sorter;
